import type { BatchLiabilityResultItem } from '@/features/cbam-reporting/hooks/cbamLiability'
import { CbamSimulationGood } from '@/features/cbam-simulations-v2/hooks/cbamSimulationGoods'
import { RowActionsMenu, TableCell, TableRow } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { useMemo } from 'react'
import CnCodeTag from '@/shared/components/CnCodeTag'
import { useNetMassDisplay } from '@/shared/netMassDisplay/NetMassDisplayUnitContext'
import ReactCountryFlag from 'react-country-flag'

type CbamSimulationGoodRowProps = {
  cbamSimulationGood: CbamSimulationGood
  onInspect: (good: CbamSimulationGood) => void
  liabilityResult: BatchLiabilityResultItem | null
  projectionYear: string
  onEdit: (good: CbamSimulationGood) => void
  onDelete: (good: CbamSimulationGood) => void
}

export default function CbamSimulationGoodRow({
  cbamSimulationGood,
  onInspect,
  liabilityResult,
  projectionYear,
  onEdit,
  onDelete
}: CbamSimulationGoodRowProps) {
  const { t, shortDateFormatter, numberFormatter3Decimals, formatCbamTco2Display } = useI18n()
  const { formatNetMass } = useNetMassDisplay()

  const projection = useMemo(() => {
    return liabilityResult?.result?.projection?.[projectionYear]
  }, [liabilityResult, projectionYear])

  const totalCertificatesDisplay = useMemo(() => {
    const net = projection?.netEmissions
    if (net === undefined || net === null) {
      return '-'
    }
    return formatCbamTco2Display(net)
  }, [projection?.netEmissions, formatCbamTco2Display])

  const certificatesPerTonneDisplay = useMemo(() => {
    const perTonne = projection?.netEmissionsPerTonne
    if (perTonne === undefined || perTonne === null) {
      return '-'
    }
    return `${numberFormatter3Decimals(perTonne)} tCO₂/t`
  }, [projection?.netEmissionsPerTonne, numberFormatter3Decimals])

  const importDateAndQuarter = useMemo(() => {
    const date = cbamSimulationGood.importDate
      ? shortDateFormatter(new Date(cbamSimulationGood.importDate))
      : '-'
    const quarter = cbamSimulationGood.quarter ?? ''
    return quarter ? `${date} ${quarter}` : date
  }, [cbamSimulationGood.importDate, cbamSimulationGood.quarter, shortDateFormatter])

  return (
    <TableRow className="text-sm">
      <TableCell>{importDateAndQuarter}</TableCell>
      <TableCell>{cbamSimulationGood.description || '-'}</TableCell>
      <TableCell>
        <p className="flex items-center gap-1">
          <ReactCountryFlag countryCode={cbamSimulationGood.country} svg />
          <span>{t(`common:countriesByIsoAlpha2.${cbamSimulationGood.country}`)}</span>
        </p>
      </TableCell>
      <TableCell>
        <CnCodeTag cnCode={cbamSimulationGood.cnCode} />
      </TableCell>
      <TableCell align="right">{formatNetMass(cbamSimulationGood.netMassInTonnes)}</TableCell>
      <TableCell align="right">{certificatesPerTonneDisplay}</TableCell>
      <TableCell align="right">{totalCertificatesDisplay}</TableCell>
      <TableCell>
        <div className="flex justify-end">
          <RowActionsMenu
            onView={() => onInspect(cbamSimulationGood)}
            onEdit={() => onEdit(cbamSimulationGood)}
            onDelete={() => onDelete(cbamSimulationGood)}
            labels={{
              view: t('common:actions.inspect'),
              edit: t('common:actions.edit'),
              delete: t('common:actions.delete')
            }}
          />
        </div>
      </TableCell>
    </TableRow>
  )
}
